import type { Call } from '@api/uc-monitoring';

export interface HeatmapCell {
  hasData: boolean;
  hour: number;
  value: number;
  /** 0 = Monday .. 6 = Sunday. */
  weekday: number;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** JS `Date#getDay()` is 0=Sunday..6=Saturday; the heatmap grid is 0=Monday..6=Sunday. */
function toMondayFirstWeekday(date: Date) {
  return (date.getDay() + 6) % 7;
}

function callInterval(call: Call) {
  const start = new Date(call.startAt);

  if (Number.isNaN(start.getTime())) {
    return null;
  }

  if (call.endAt) {
    const end = new Date(call.endAt);

    return Number.isNaN(end.getTime()) ? null : { end, start };
  }

  if (call.durationSeconds) {
    return { end: new Date(start.getTime() + call.durationSeconds * 1000), start };
  }

  return { end: start, start };
}

/**
 * Midnight of `date`'s own day, or of the next day when `date` carries a time-of-day. `periodStart`
 * is usually "now minus N days", so skipping its partial first day keeps a "last N days" range at
 * exactly N buckets instead of N+1.
 */
function startOfFirstFullDay(date: Date) {
  const start = new Date(date);
  const wasMidnight = start.getHours() === 0 && start.getMinutes() === 0 && start.getSeconds() === 0 && start.getMilliseconds() === 0;

  start.setHours(0, 0, 0, 0);

  if (!wasMidnight) {
    start.setDate(start.getDate() + 1);
  }

  return start;
}

function countWeekdayOccurrences(periodStart: Date, periodEnd: Date) {
  const counts = new Array(7).fill(0);
  const cursor = startOfFirstFullDay(periodStart);

  while (cursor < periodEnd) {
    counts[toMondayFirstWeekday(cursor)]++;
    cursor.setDate(cursor.getDate() + 1);
  }

  return counts;
}

/**
 * Interim FE aggregation until HD-3018 ships (see HD-3026 comments). Buckets by browser-local
 * time, since room-local time isn't on the FE model yet.
 */
export function aggregateCallsToWeeklyHeatmap(calls: Call[], periodStart: Date, periodEnd: Date) {
  const weekdayOccurrences = countWeekdayOccurrences(periodStart, periodEnd);
  const sums = Array.from({ length: 7 }, () => new Array(24).fill(0));

  for (const call of calls) {
    const interval = callInterval(call);

    if (!interval) {
      continue;
    }

    const headcount = call.participantCount ?? 0;
    const start = interval.start < periodStart ? periodStart : interval.start;
    const end = interval.end > periodEnd ? periodEnd : interval.end;

    let cursor = new Date(start);

    while (cursor < end) {
      const hourStart = new Date(cursor);

      hourStart.setMinutes(0, 0, 0);

      const hourEnd = new Date(hourStart.getTime() + HOUR_MS);
      const overlapEnd = end < hourEnd ? end : hourEnd;
      const overlapHours = (overlapEnd.getTime() - cursor.getTime()) / HOUR_MS;

      sums[toMondayFirstWeekday(cursor)][cursor.getHours()] += headcount * overlapHours;

      cursor = hourEnd;
    }
  }

  const cells: HeatmapCell[] = [];

  for (let weekday = 0; weekday < 7; weekday++) {
    const occurrences = weekdayOccurrences[weekday];

    for (let hour = 0; hour < 24; hour++) {
      cells.push({
        hasData: occurrences > 0,
        hour,
        value: occurrences > 0 ? sums[weekday][hour] / occurrences : 0,
        weekday,
      });
    }
  }

  return cells;
}

function toLocalDateKey(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function aggregateCallsByDay(calls: Call[], periodStart: Date, periodEnd: Date, valueOf: (call: Call) => number | null) {
  const byDay = new Map<string, { seen: boolean; sum: number }>();
  const cursor = startOfFirstFullDay(periodStart);

  while (cursor < periodEnd) {
    byDay.set(toLocalDateKey(cursor), {
      seen: false,
      sum: 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  for (const call of calls) {
    const start = new Date(call.startAt);

    if (Number.isNaN(start.getTime()) || start < periodStart || start >= periodEnd) {
      continue;
    }

    const bucket = byDay.get(toLocalDateKey(start));
    const value = valueOf(call);

    // The partial first day has no bar (see startOfFirstFullDay); its calls must not append one after today.
    if (!bucket || value === null) {
      continue;
    }

    bucket.seen = true;
    bucket.sum += value;
  }

  return Array.from(byDay.entries()).map(([date, bucket]) => ({
    bucket,
    date,
  }));
}

export function aggregateCallsToDailyHeadcount(calls: Call[], periodStart: Date, periodEnd: Date) {
  return aggregateCallsByDay(calls, periodStart, periodEnd, call => call.participantCount ?? 0).map(({ bucket, date }) => ({
    date,
    headcount: bucket.sum,
  }));
}

/** `headcount` is null for a day without any sensor reading, so a missing reading is not drawn as 0. */
export function aggregateCallsToDailyTrackedHeadcount(calls: Call[], periodStart: Date, periodEnd: Date) {
  return aggregateCallsByDay(calls, periodStart, periodEnd, call => call.tracked ?? null).map(({ bucket, date }) => ({
    date,
    headcount: bucket.seen ? bucket.sum : null,
  }));
}

/**
 * One point per hour of `day`, summing participant counts by the hour a call started. HD-3026's
 * text conflicts ("one bar per hour" vs. "same component as the report page's per-day chart") -
 * picked the hourly-bars reading, flagged alongside the ticket's other data gaps.
 */
export function aggregateCallsToHourlyHeadcount(calls: Call[], day: Date) {
  return aggregateCallsByHour(calls, day, call => call.participantCount ?? 0);
}

/**
 * Same hourly buckets as `aggregateCallsToHourlyHeadcount`, from the occupancy sensor reading (`Call.tracked`).
 * `headcount` is null for an hour without any sensor reading, so a missing reading is not drawn as 0.
 */
export function aggregateCallsToHourlyTrackedHeadcount(calls: Call[], day: Date) {
  return aggregateCallsByHour(calls, day, call => call.tracked ?? null, true);
}

function aggregateCallsByHour(calls: Call[], day: Date, getValue: (call: Call) => number | null, emptyAsNull = false) {
  const dayStart = new Date(day);

  dayStart.setHours(0, 0, 0, 0);

  const dayEnd = new Date(dayStart.getTime() + DAY_MS);
  const counts = new Array<number>(24).fill(0);
  const seen = new Array<boolean>(24).fill(false);

  for (const call of calls) {
    const start = new Date(call.startAt);
    const value = getValue(call);

    if (Number.isNaN(start.getTime()) || start < dayStart || start >= dayEnd || value === null) {
      continue;
    }

    counts[start.getHours()] += value;
    seen[start.getHours()] = true;
  }

  return counts.map((sum, hour) => ({
    headcount: emptyAsNull && !seen[hour] ? null : sum,
    hour,
  }));
}
